// src/components/Admin/HistoricoCambiosAdmin.jsx
import React, { useEffect, useState } from "react";
import {
    Paper, Tabs, Tab, Toolbar, Typography, IconButton, Tooltip, Button,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox,
    FormControl, InputLabel, Select, MenuItem, TablePagination, Chip, Stack, Link,
    CircularProgress
} from "@mui/material";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import SortIcon from "@mui/icons-material/Sort";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PhotoIcon from "@mui/icons-material/Photo";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import AntiguoHistoricoAdmin from "./AntiguoHistoricoAdmin";
import { obtenerBICdesdeBanco } from "../common/Bics";

const API = process.env.REACT_APP_API_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}` });
const abreviar = (v, n = 80) => (v && v.length > n ? v.slice(0, n - 1) + "…" : v || "");
const mascaraSiPassword = (campo, v) => (campo || "").toLowerCase() === "password" ? "••••••" : (v ?? "");

const readHeaderTotal = (res) => {
    const h = res?.headers || {};
    const candidates = [
        "x-total-count", "x-total", "x-total-elements", "x-totalrecords", "x-total-pages-items"
    ];
    for (const k of candidates) {
        const v = h[k];
        if (v != null && !Number.isNaN(parseInt(v, 10))) return parseInt(v, 10);
    }
    return null;
};

// Normaliza una página devolviendo {content, totalElements, totalPages}
const normalizeFromResponse = (resData, { page, size }) => {
    const content = resData?.content ?? resData?.items ?? resData?.data ?? (Array.isArray(resData) ? resData : []) ?? [];
    const totalElements =
        resData?.totalElements ??
        resData?.total ??
        resData?.count ??
        resData?.totalCount ??
        resData?.page?.totalElements ??
        null;

    const totalPages =
        resData?.totalPages ??
        (Number.isFinite(Number(totalElements)) ? Math.max(1, Math.ceil(Number(totalElements) / (size || 25))) : null);

    return { content, totalElements, totalPages };
};

const safeCount = (pageObj) => {
    const te = Number(pageObj?.totalElements);
    const len = pageObj?.content?.length ?? 0;
    return te > 0 ? te : len;
};

/* ------------------------------ helpers paginación ------------------------------ */
const normalizePage = (raw, { page = 0, size = 25 } = {}) => {
    const content =
        raw?.content ?? raw?.items ?? raw?.data ?? (Array.isArray(raw) ? raw : []) ?? [];

    const nSize = Number(size) || 25;
    const nPage = Number(page) || 0;

    let total =
        raw?.totalElements ??
        raw?.total ??
        raw?.count ??
        raw?.totalCount ??
        raw?.total_records ??
        raw?.page?.totalElements ??
        raw?.page?.total ??
        null;

    if (total == null || Number.isNaN(Number(total))) {
        const len = content.length;
        total = (len >= nSize) ? (nPage + 2) * nSize : nPage * nSize + len;
    } else {
        total = Number(total);
        if (total === content.length && content.length === nSize) {
            total = (nPage + 2) * nSize;
        }
    }

    const rawTotalPages = raw?.totalPages;
    const totalPages = Number.isFinite(Number(rawTotalPages))
        ? Math.max(1, Number(rawTotalPages))
        : Math.max(1, Math.ceil(total / nSize));

    return { content, totalElements: total, totalPages };
};

const normalizeWithPeek = async ({
    endpoint,
    firstData,
    baseParams,
    page,
    size,
    headers
}) => {
    const norm = normalizePage(firstData, { page, size });
    const len = norm.content.length;

    if (len < size) {
        const total = page * size + len;
        return {
            ...norm,
            totalElements: total,
            totalPages: Math.max(1, Math.ceil(total / size)),
        };
    }

    try {
        const peekParams = { ...baseParams, page: page + 1, size: 1 };
        const peekRes = await axios.get(endpoint, { headers, params: peekParams });
        const peek = normalizePage(peekRes.data, { page: page + 1, size: 1 });
        const hasNext = (peek.content?.length ?? 0) > 0;

        if (!hasNext) {
            const total = (page + 1) * size;
            return {
                ...norm,
                totalElements: total,
                totalPages: page + 1,
            };
        }
        const est = (page + 2) * size;
        return {
            ...norm,
            totalElements: est,
            totalPages: Math.max(norm.totalPages, page + 2),
        };
    } catch {
        return norm;
    }
};

const estimateCount = (totalFromServer, contentLen, page, size) => {
    const nSize = Number(size) || 25;
    const nPage = Number(page) || 0;
    const totalNum = Number(totalFromServer);

    if (Number.isFinite(totalNum) && totalNum > 0) {
        if (totalNum === contentLen && contentLen === nSize) {
            return (nPage + 2) * nSize;
        }
        return totalNum;
    }
    return (contentLen === nSize) ? (nPage + 2) * nSize : nPage * nSize + contentLen;
};

/* ---------- Diálogo para rechazar con comentario ---------- */
function RechazoDialog({ open, onClose, onConfirm, defaultComentario = "" }) {
    const [comentario, setComentario] = React.useState(defaultComentario);
    React.useEffect(() => { if (open) setComentario(defaultComentario); }, [open, defaultComentario]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Rechazar solicitudes</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Escribe un comentario para el rechazo (se guardará en el histórico).
                </Typography>
                <TextField
                    autoFocus multiline minRows={3} fullWidth
                    value={comentario}
                    onChange={(e) => setComentario(e.target.value)}
                    placeholder="Motivo del rechazo..."
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button color="error" variant="contained" onClick={() => onConfirm(comentario)}>Rechazar</Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------- Helpers valores ----------
const getAnteriorConFallback = (row) => {
    if (!row) return "";
    const campo = row?.campo;
    const directo = row?.valorAnterior ?? row?.valor_anterior;
    if (directo != null && String(directo).trim() !== "") return directo;
    const col = row?.colegiado || {};
    let fromProfile = campo ? col[campo] : undefined;
    if (Array.isArray(fromProfile)) fromProfile = fromProfile.join(",");
    return fromProfile != null ? String(fromProfile) : "";
};
const formateaCambioPreview = (row) => {
    const campo = row?.campo ?? "(campo)";
    const oldV = mascaraSiPassword(campo, getAnteriorConFallback(row));
    const newV = mascaraSiPassword(campo, row?.valorNuevo ?? row?.valor_nuevo ?? row?.valor);
    return `${campo}: de "${oldV}" a "${newV}"`;
};

const mapPendSortField = (field) => {
    if (!field) return "fechaSolicitud";
    return field === "creadoEl" ? "fechaSolicitud" : field; // compat
};

const dmyToIso = (texto) => {
    const m = texto.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (!m) return null;

    return `${m[3]}-${m[2]}-${m[1]}`;
};

const sanitizePdfText = (s) =>
    (s ?? "")
        .toString()
        .replace(/\u2192/g, "->")          // →  -> 
        .replace(/[\u2013\u2014]/g, "-")   // – — -> -
        .replace(/[\u2018\u2019]/g, "'")   // ‘ ’ -> '
        .replace(/[\u201C\u201D]/g, '"')   // “ ” -> "
        .replace(/\u2026/g, "...")         // … -> ...
        .replace(/\u00A0/g, " ");          // NBSP -> space

/* ------- Helpers de ESTADO: parsear “de "X" a "Y"” desde comentario_admin ------- */
const parseCambioEstado = (comentarioRaw) => {
    const txt = (comentarioRaw || "").replace(/\s+/g, " ").trim();
    if (!txt) return null;

    // Acepta vacío entre comillas ("") y comillas curvas “”
    const patterns = [
        /ESTADO[^:]*:\s*de\s*["“”]([^"”]*)["“”]\s*a\s*["“”]([^"”]+)["“”]/i,
        /ESTADO[^:]*:\s*de\s*'([^']*)'\s*a\s*'([^']+)'/i,
        /ESTADO[^-–—]*[-–—]\s*ESTADO[^:]*:\s*de\s*["“”]([^"”]*)["“”]\s*a\s*["“”]([^"”]+)["“”]/i,
        /ESTADO[^:]*\sde\s["“”]([^"”]*)["“”]\s+a\s+["“”]([^"”]+)["“”]/i,
    ];
    for (const re of patterns) {
        const m = txt.match(re);
        if (m && m[1] !== undefined && m[2] !== undefined) {
            return { from: (m[1] || "").trim(), to: (m[2] || "").trim() };
        }
    }
    return null;
};

const isAltaNuevaColegiacion = (comentarioRaw) =>
    /alta\s+nueva\s+colegiaci[oó]n/i.test(comentarioRaw || "") ||
    /alta\s+nuevo\s+ingreso/i.test(comentarioRaw || "");

const isEstadoNuevoFromParsed = (p) =>
    !p || !p.from || String(p.from).trim() === "" || p.from === "—" || p.from === '""';

const isAlta = (s) => /^alta\b/i.test(s || "");
const isBaja = (s) => /^baja\b/i.test(s || "");
const isDefuncion = (s) => /defunci[oó]n/i.test(s || "");

/* ------- Helpers FECHA ESTADO ------- */
const normalizeDashValue = (v) => {
    const s = (v ?? "").toString().trim();
    return s === "—" || s === "-" ? "" : s;
};

const extractIsoDate = (text) => {
    if (!text) return "";
    const m = text.toString().match(/\b(\d{4}-\d{2}-\d{2})\b/);
    return m ? m[1] : "";
};

const toIsoDate = (value) => {
    if (!value) return "";

    const s = String(value).trim();

    // YYYY-MM-DD
    const isoMatch = s.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
    if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }

    // DD/MM/YYYY
    const esMatch = s.match(/\b(\d{2})\/(\d{2})\/(\d{4})\b/);
    if (esMatch) {
        return `${esMatch[3]}-${esMatch[2]}-${esMatch[1]}`;
    }

    // Intento genérico Date
    const d = new Date(s);
    if (!isNaN(d)) {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    }

    return "";
};

const parseCampoFechaCambio = (comentarioRaw, label) => {
    const txt = (comentarioRaw || "").replace(/\s+/g, " ").trim();
    if (!txt) return null;

    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const patterns = [
        new RegExp(`${escaped}\\s*:\\s*de\\s*["“”]([^"”]*)["“”]\\s*a\\s*["“”]([^"”]*)["“”]`, "i"),
        new RegExp(`${escaped}\\s*:\\s*de\\s*'([^']*)'\\s*a\\s*'([^']*)'`, "i"),
    ];

    for (const re of patterns) {
        const m = txt.match(re);
        if (m) {
            return {
                from: normalizeDashValue((m[1] || "").trim()),
                to: normalizeDashValue((m[2] || "").trim()),
            };
        }
    }
    return null;
};

const getFechaEstadoIso = (row) => {
    const comentarioAdmin = row?.comentarioAdmin ?? row?.comentario_admin ?? "";
    const parsedEstado = parseCambioEstado(comentarioAdmin);

    let esEstado = !!parsedEstado;
    let esEstadoNuevo = esEstado && isEstadoNuevoFromParsed(parsedEstado);

    if (!esEstado && isAltaNuevaColegiacion(comentarioAdmin)) {
        esEstado = true;
        esEstadoNuevo = true;
    }

    if (!esEstado) return "";

    const cambioFechaAlta = parseCampoFechaCambio(comentarioAdmin, "FECHA ALTA COLEGIACIÓN");
    const cambioFechaBaja = parseCampoFechaCambio(comentarioAdmin, "FECHA BAJA COLEGIACIÓN");

    const fallbackFechaFila = toIsoDate(row?.fecha || row?.creadoEl || row?.creado_el || "");

    // Estado NUEVO o alta nueva: usar fecha alta si existe; si no, fecha de la fila
    if (esEstadoNuevo) {
        return (
            fallbackFechaFila
        );
    }

    const estadoDestino = parsedEstado?.to || "";

    // Si el destino es ALTA, usar FECHA ALTA
    if (isAlta(estadoDestino)) {
        return (
            extractIsoDate(cambioFechaAlta?.to) ||
            extractIsoDate(cambioFechaBaja?.to) ||
            fallbackFechaFila
        );
    }

    // Si el destino es BAJA o DEFUNCIÓN, usar FECHA BAJA
    if (isBaja(estadoDestino) || isDefuncion(estadoDestino)) {
        return (
            extractIsoDate(cambioFechaBaja?.to) ||
            extractIsoDate(cambioFechaAlta?.to) ||
            fallbackFechaFila
        );
    }

    // Fallback genérico
    return (
        extractIsoDate(cambioFechaAlta?.to) ||
        extractIsoDate(cambioFechaBaja?.to) ||
        fallbackFechaFila
    );
};

const isDateInRangeInclusive = (isoDate, desde, hasta) => {
    if (!isoDate) return false;
    if (desde && isoDate < desde) return false;
    if (hasta && isoDate > hasta) return false;
    return true;
};

const enrichHistoricoRow = (h) => {
    const comentarioAdmin = h?.comentarioAdmin ?? h?.comentario_admin ?? "";
    const parsed = parseCambioEstado(comentarioAdmin);

    let esEstado = !!parsed;
    let esEstadoNuevo = esEstado && isEstadoNuevoFromParsed(parsed);

    if (!esEstado && isAltaNuevaColegiacion(comentarioAdmin)) {
        esEstado = true;
        esEstadoNuevo = true;
    }

    const fechaEstadoIso = getFechaEstadoIso(h);
    const fechaEstadoTexto = fechaEstadoIso ? formatDateES(fechaEstadoIso) : "—";

    return {
        ...h,
        _comentarioAdmin: comentarioAdmin,
        _parsedEstado: parsed,
        _esEstado: esEstado,
        _esEstadoNuevo: esEstadoNuevo,
        _fechaEstadoIso: fechaEstadoIso,
        _fechaEstadoTexto: fechaEstadoTexto,
    };
};

const formatDateES = (v) => {
    if (!v) return "-";
    const iso = extractIsoDate(v);
    if (iso) {
        const [y, m, d] = iso.split("-");
        return `${d}/${m}/${y}`;
    }
    const d = new Date(v);
    if (isNaN(d)) return String(v);
    return new Intl.DateTimeFormat("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    }).format(d);
};

/* ---------- Fila pendientes ---------- */
function FilaPendiente({ row, selected, toggle }) {
    const colegiadoCodigo = row?.colegiado?.codigo ?? row?.colegiadoCodigo ?? "-";
    const colegiadoNombre =
        (row?.colegiado?.nombreCompleto ??
            [row?.colegiado?.nombre, row?.colegiado?.apellido_1, row?.colegiado?.apellido_2].filter(Boolean).join(" ")
        ) || row?.colegiadoNombre || "-";

    const fecha = row?.fecha ?? row?.creado_el ?? row?.creadoEl ?? "";
    const campo = row?.campo ?? "-";
    const valorAnterior = getAnteriorConFallback(row);
    const valorNuevo = row?.valorNuevo ?? row?.valor_nuevo ?? row?.valor ?? "";
    const esFoto = (campo || "").toLowerCase() === "foto";

    return (
        <TableRow hover>
            <TableCell padding="checkbox">
                <Checkbox checked={selected} onChange={() => toggle(row.id)} />
            </TableCell>
            <TableCell>{row.id}</TableCell>
            <TableCell>
                <Stack spacing={0.3}>
                    <Typography fontWeight={600}>{colegiadoNombre}</Typography>
                    <Typography variant="caption" color="text.secondary">Código: {colegiadoCodigo}</Typography>
                </Stack>
            </TableCell>
            <TableCell>
                <Stack spacing={0.3}>
                    <Typography fontWeight={600}>{campo}</Typography>
                    <Typography variant="caption" color="text.secondary">{fecha?.toString()}</Typography>
                </Stack>
            </TableCell>
            <TableCell>
                <Stack spacing={0.6}>
                    {valorAnterior ? <Chip size="small" label={`Antes: ${abreviar(valorAnterior, 80)}`} /> : null}
                    <Chip size="small" variant="outlined" label={`Nuevo: ${abreviar(valorNuevo, 80)}`} />
                    <Typography variant="caption" color="text.secondary"></Typography>
                    {formateaCambioPreview(row)}
                    {esFoto && (
                        <Stack direction="row" spacing={1}>
                            <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => row.onPreview("nuevo")}>Ver foto nueva</Button>
                            {valorAnterior ? (
                                <Button size="small" variant="text" startIcon={<PhotoIcon />} onClick={() => row.onPreview("anterior")}>Ver anterior</Button>
                            ) : null}
                        </Stack>
                    )}
                </Stack>
            </TableCell>
            <TableCell align="right">
                <Tooltip title="Aprobar"><IconButton color="success" onClick={row.onApprove}><CheckCircleIcon /></IconButton></Tooltip>
                <Tooltip title="Rechazar"><IconButton color="error" onClick={row.onReject}><CancelIcon /></IconButton></Tooltip>
            </TableCell>
        </TableRow>
    );
}

/* ------------------------------ Principal ------------------------------ */
export default function HistoricoCambiosAdmin() {
    const [tab, setTab] = useState(0); // 0=Pendientes, 1=Histórico

    // Formateo fecha DD/MM/YYYY
    const formatDDMMYYYY = (v) => formatDateES(v);

    // ---- Filtros HISTÓRICO ----
    const [histTipo, setHistTipo] = useState("");        // "", "ESTADO", "CAMBIO"
    const [histDesde, setHistDesde] = useState("");      // "YYYY-MM-DD"
    const [histHasta, setHistHasta] = useState("");      // "YYYY-MM-DD"
    const [histQ, setHistQ] = useState("");              // búsqueda libre
    const [histFechaEstadoDesde, setHistFechaEstadoDesde] = useState("");
    const [histFechaEstadoHasta, setHistFechaEstadoHasta] = useState("");

    // ---- PENDIENTES ----
    const [pendPage, setPendPage] = useState(0);
    const [pendRows, setPendRows] = useState(25);
    const [pendSortBy, setPendSortBy] = useState("fechaSolicitud");
    const [pendSortDir, setPendSortDir] = useState("DESC");
    const [pendLoading, setPendLoading] = useState(false);
    const [pendData, setPendData] = useState({ content: [], totalElements: 0, totalPages: 0 });

    const [fColeg, setFColeg] = useState("");
    const [fEstado, setFEstado] = useState("PENDIENTE");
    const [fDesde, setFDesde] = useState("");
    const [fHasta, setFHasta] = useState("");

    const [selIds, setSelIds] = useState([]);
    const [rechazoOpen, setRechazoOpen] = useState(false);
    const [rejectTargetIds, setRejectTargetIds] = useState([]);
    const [pendTotalFixed, setPendTotalFixed] = useState(null);
    const [histTotalFixed, setHistTotalFixed] = useState(null);

    // ---- HISTÓRICO ----
    const [hist, setHist] = useState({ content: [], totalElements: 0, totalPages: 0 });
    const [histLoading, setHistLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState(25);
    const [histSortBy, setHistSortBy] = useState("fecha");
    const [histSortDir, setHistSortDir] = useState("desc");
    const [histCodigo, setHistCodigo] = useState("");

    // UI
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

    // Preview foto
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState("");
    const [previewTitle, setPreviewTitle] = useState("");
    const [previewLoading, setPreviewLoading] = useState(false);
    const [objectUrlToRevoke, setObjectUrlToRevoke] = useState(null);

    const eliminarHistoricoPorSql = async (id) => {
        if (!window.confirm(`¿Eliminar el registro con ID ${id}?`)) return;

        try {
            await axios.post(
                `${API}/api/admin/sql/exec`,
                {
                    sql: `DELETE FROM historico_cambios WHERE id = ${Number(id)}`,
                    modo: "DML",
                    limite: 1
                },
                { headers: authHeaders() }
            );

            setSnack({
                open: true,
                msg: `Registro ${id} eliminado`,
                sev: "success"
            });

            fetchHistorico();
        } catch (e) {
            setSnack({
                open: true,
                msg: e?.response?.data?.message || "Error eliminando registro",
                sev: "error"
            });
        }
    };


    const editarFechaHistoricoPorSql = async (h, fechaNueva) => {
        if (!fechaNueva) return;

        if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaNueva)) {
            setSnack({
                open: true,
                msg: "Formato inválido. Usa YYYY-MM-DD",
                sev: "error"
            });
            return;
        }

        if (!window.confirm(`¿Cambiar la fecha del registro ${h.id} a ${fechaNueva}?`)) return;

        const escapeSql = (v) => String(v ?? "").replaceAll("'", "''");

        const comentarioActual =
            h?._comentarioAdmin ??
            h?.comentarioAdmin ??
            h?.comentario_admin ??
            "";

        let sql;

        const tieneFechaEstado =
            /FECHA\s+ALTA\s+COLEGIACI[OÓ]N\s*:/i.test(comentarioActual) ||
            /FECHA\s+BAJA\s+COLEGIACI[OÓ]N\s*:/i.test(comentarioActual);

        if (tieneFechaEstado) {
            const comentarioNuevo = comentarioActual.replace(
                /(FECHA\s+(?:ALTA|BAJA)\s+COLEGIACI[OÓ]N\s*:\s*de\s*["“”][^"”]*["“”]\s*a\s*["“”])([^"”]*)(["“”])/i,
                `$1${fechaNueva}$3`
            );

            sql = `UPDATE historico_cambios 
           SET comentario_admin = '${escapeSql(comentarioNuevo)}',
               fecha = '${fechaNueva}'
           WHERE id = ${Number(h.id)}`;
        } else {
            sql = `UPDATE historico_cambios 
           SET fecha = '${fechaNueva}'
           WHERE id = ${Number(h.id)}`;
        }

        try {
            await axios.post(
                `${API}/api/admin/sql/exec`,
                {
                    sql,
                    modo: "DML",
                    limite: 1
                },
                { headers: authHeaders() }
            );

            setSnack({
                open: true,
                msg: `Fecha del registro ${h.id} actualizada`,
                sev: "success"
            });

            fetchHistorico();
        } catch (e) {
            setSnack({
                open: true,
                msg: e?.response?.data?.message || "Error actualizando fecha",
                sev: "error"
            });
        }
    };

    const toApiUrl = (relativePath) => {
        const base = (API || "").replace(/\/+$/, "");
        const rel = (relativePath || "").replace(/^\/+/, "");
        return `${base}/${rel}`;
    };
    const resolveImageUrl = (value) => {
        if (!value) return "";
        const v = value.toString().trim();
        if (/^https?:\/\//i.test(v)) return v;
        return toApiUrl(v);
    };
    const fetchImageWithAuth = async (url) => {
        const res = await axios.get(url, { headers: authHeaders(), responseType: "blob" });
        const blobUrl = URL.createObjectURL(res.data);
        setObjectUrlToRevoke(blobUrl);
        return blobUrl;
    };
    const openPreview = async (row, which) => {
        const isFoto = (row?.campo || "").toLowerCase() === "foto";
        if (!isFoto) return;
        const raw = which === "anterior"
            ? (row?.valorAnterior ?? row?.valor_anterior)
            : (row?.valorNuevo ?? row?.valor_nuevo ?? row?.valor);

        const colegiadoNombre =
            row?.colegiado?.nombreCompleto ??
            [row?.colegiado?.nombre, row?.colegiado?.apellido_1, row?.colegiado?.apellido_2].filter(Boolean).join(" ");

        setPreviewTitle(`${which === "anterior" ? "Foto anterior" : "Foto nueva"} — ${colegiadoNombre || "Colegiado"}`);
        setPreviewUrl("");
        setPreviewLoading(true);
        setPreviewOpen(true);

        try {
            const directUrl = resolveImageUrl(raw);
            const probe = await fetch(directUrl, { method: "HEAD" });
            if (probe.ok) setPreviewUrl(directUrl);
            else setPreviewUrl(await fetchImageWithAuth(directUrl));
        } catch {
            try {
                const directUrl = resolveImageUrl(raw);
                setPreviewUrl(await fetchImageWithAuth(directUrl));
            } catch {
                setSnack({ open: true, msg: "No se pudo cargar la imagen", sev: "error" });
                setPreviewOpen(false);
            }
        } finally {
            setPreviewLoading(false);
        }
    };
    const closePreview = () => {
        setPreviewOpen(false);
        if (objectUrlToRevoke) { URL.revokeObjectURL(objectUrlToRevoke); setObjectUrlToRevoke(null); }
        setPreviewUrl(""); setPreviewTitle("");
    };

    /* -------------------- Datos: Pendientes -------------------- */
    const fetchPendientes = async () => {
        try {
            setPendLoading(true);
            const endpoint = `${API}/api/cambios/admin/solicitudes-cambio/page`;

            const baseParams = { sort: mapPendSortField(pendSortBy), dir: pendSortDir };
            if (fColeg?.toString().trim()) baseParams.codigo = parseInt(fColeg, 10) || undefined;
            if (fEstado?.trim()) baseParams.estado = fEstado.trim();
            if (fDesde) baseParams.desde = fDesde;
            if (fHasta) baseParams.hasta = fHasta;

            const res = await axios.get(endpoint, {
                headers: authHeaders(),
                params: { ...baseParams, page: pendPage, size: pendRows },
            });

            const data = res.data || {};

            const content =
                data?.content ??
                data?.items ??
                data?.data ??
                (Array.isArray(data) ? data : []) ?? [];

            let total =
                data?.totalElements ??
                data?.total_elements ??
                data?.total ??
                data?.count ??
                data?.totalCount ??
                data?.page?.totalElements ??
                data?.page?.total_elements ??
                readHeaderTotal(res);

            total = Number.isFinite(Number(total)) ? Number(total) : null;

            let totalPages =
                data?.totalPages ??
                data?.total_pages ??
                (Number.isFinite(total) ? Math.max(1, Math.ceil(total / (pendRows || 25))) : null);

            totalPages = Number.isFinite(Number(totalPages))
                ? Number(totalPages)
                : null;

            if (total == null || totalPages == null) {
                const norm = await normalizeWithPeek({
                    endpoint,
                    firstData: data,
                    baseParams,
                    page: pendPage,
                    size: pendRows,
                    headers: authHeaders(),
                });
                setPendData(norm);
                setSelIds([]);
                return;
            }

            const norm = { content, totalElements: total, totalPages };
            setPendData(norm);
            setSelIds([]);
        } catch (e) {
            console.error(e);
            setSnack({ open: true, msg: "Error cargando pendientes", sev: "error" });
        } finally {
            setPendLoading(false);
        }
    };

    /* ---------------- Export pendientes a Excel ---------------- */
    const exportPendientesToExcel = async () => {
        try {
            const pageSize = 1000;
            let pageIdx = 0;
            let allRows = [];
            let totalPages = 1;

            do {
                const params = {
                    page: pageIdx, size: pageSize, sort: mapPendSortField(pendSortBy), dir: pendSortDir,
                    codigo: fColeg?.toString().trim() ? parseInt(fColeg, 10) : undefined,
                    estado: fEstado?.trim() || undefined,
                    desde: fDesde || undefined, hasta: fHasta || undefined,
                };
                const res = await axios.get(`${API}/api/cambios/admin/solicitudes-cambio/page`, {
                    headers: authHeaders(), params,
                });
                const pageObj = normalizePage(res.data, { page: pageIdx, size: pageSize });
                allRows = allRows.concat(pageObj.content);
                totalPages = pageObj.totalPages ?? 1;
                pageIdx += 1;
            } while (pageIdx < totalPages);

            if (!allRows.length) {
                setSnack({ open: true, msg: "No hay datos que exportar", sev: "info" });
                return;
            }

            const rowsForXlsx = allRows.map((r) => {
                const colegiadoCodigo = r?.colegiado?.codigo ?? r?.colegiadoCodigo ?? "";
                const colegiadoNombre =
                    (r?.colegiado?.nombreCompleto ??
                        [r?.colegiado?.nombre, r?.colegiado?.apellido_1, r?.colegiado?.apellido_2].filter(Boolean).join(" ")
                    ) || r?.colegiadoNombre || "";

                return {
                    ID: r?.id ?? "",
                    "Código colegiado": colegiadoCodigo,
                    "Nombre colegiado": colegiadoNombre,
                    Campo: r?.campo ?? "",
                    "Valor anterior": r?.valorAnterior ?? r?.valor_anterior ?? "",
                    "Valor nuevo": r?.valorNuevo ?? r?.valor_nuevo ?? r?.valor ?? "",
                    Estado: r?.estado ?? "",
                    "Creado el": (r?.creadoEl || r?.creado_el || r?.fecha || "")?.toString() ?? "",
                };
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rowsForXlsx);
            XLSX.utils.book_append_sheet(wb, ws, "Pendientes");
            const colWidths = Object.keys(rowsForXlsx[0]).map((key) => ({
                wch: Math.max(key.length, ...rowsForXlsx.map((row) => (row[key] ? row[key].toString().length : 0))) + 2,
            }));
            ws["!cols"] = colWidths;
            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            saveAs(new Blob([wbout], { type: "application/octet-stream" }),
                `solicitudes_pendientes_${new Date().toISOString().slice(0, 10)}.xlsx`);
            setSnack({ open: true, msg: "Exportación completada", sev: "success" });
        } catch {
            setSnack({ open: true, msg: "Error al exportar", sev: "error" });
        }
    };

    /* -------------------- Datos: Histórico -------------------- */
    const fetchHistorico = async () => {
        try {
            setHistLoading(true);
            const endpoint = `${API}/api/historico-cambios/admin/todos`;

            const baseParams = { sortBy: histSortBy, order: histSortDir };
            const codigoNum = parseInt(histCodigo, 10);
            if (!Number.isNaN(codigoNum)) baseParams.codigo = codigoNum;
            if (histTipo) baseParams.tipo = histTipo;
            if (histDesde) baseParams.desde = histDesde;
            if (histHasta) baseParams.hasta = histHasta;
            if (histQ && histQ.trim().length >= 2) baseParams.q = histQ.trim();

            const hayFiltroFechaEstado = !!(histFechaEstadoDesde || histFechaEstadoHasta);

            // Sin filtro de fecha estado: paginación normal del backend
            if (!hayFiltroFechaEstado) {
                const res = await axios.get(endpoint, {
                    headers: authHeaders(),
                    params: { ...baseParams, page, size: rows },
                });

                const data = res.data || {};
                const total =
                    data.totalElements ?? data.total_elements ??
                    data.total ?? data.count ?? data.totalCount ??
                    data.page?.totalElements ?? data.page?.total_elements ?? null;
                const pages =
                    data.totalPages ?? data.total_pages ??
                    (Number.isFinite(Number(total)) ? Math.max(1, Math.ceil(Number(total) / (rows || 25))) : 1);

                const contentBase = Array.isArray(data.content) ? data.content : [];
                const content = contentBase.map(enrichHistoricoRow);

                setHist({
                    content,
                    totalElements: Number(total ?? 0),
                    totalPages: Number(pages),
                });
                return;
            }

            // Con filtro de fecha estado: traer todo lo filtrable y paginar en front
            const pageSize = 1000;
            let pageIdx = 0;
            let totalPages = 1;
            let allRows = [];

            do {
                const res = await axios.get(endpoint, {
                    headers: authHeaders(),
                    params: { ...baseParams, page: pageIdx, size: pageSize },
                });

                const pageObj = normalizePage(res.data, { page: pageIdx, size: pageSize });
                allRows = allRows.concat(pageObj.content || []);
                totalPages = pageObj.totalPages ?? 1;
                pageIdx += 1;
            } while (pageIdx < totalPages);

            const enriched = allRows.map(enrichHistoricoRow);

            const filtered = enriched.filter((r) => {
                if (!r._esEstado) return false;
                return isDateInRangeInclusive(
                    r._fechaEstadoIso,
                    histFechaEstadoDesde,
                    histFechaEstadoHasta
                );
            });

            const start = page * rows;
            const end = start + rows;
            const pageContent = filtered.slice(start, end);

            setHist({
                content: pageContent,
                totalElements: filtered.length,
                totalPages: Math.max(1, Math.ceil(filtered.length / rows)),
            });
        } catch (e) {
            console.error(e);
            setSnack({ open: true, msg: "Error cargando histórico", sev: "error" });
        } finally {
            setHistLoading(false);
        }
    };

    // --------- Export HISTÓRICO a Excel (con filtros actuales) ---------
    const exportHistoricoToExcel = async () => {
        try {
            const endpoint = `${API}/api/historico-cambios/admin/todos`;
            const pageSize = 1000;
            let pageIdx = 0;
            let allRows = [];
            let totalPages = 1;

            const baseParams = { sortBy: histSortBy, order: histSortDir };
            const codigoNum = parseInt(histCodigo, 10);
            if (!Number.isNaN(codigoNum)) baseParams.codigo = codigoNum;
            if (histTipo) baseParams.tipo = histTipo;
            if (histDesde) baseParams.desde = histDesde;
            if (histHasta) baseParams.hasta = histHasta;
            if (histQ && histQ.trim().length >= 2) baseParams.q = histQ.trim();

            do {
                const res = await axios.get(endpoint, {
                    headers: authHeaders(),
                    params: { ...baseParams, page: pageIdx, size: pageSize },
                });
                const pageObj = normalizePage(res.data, { page: pageIdx, size: pageSize });
                allRows = allRows.concat(pageObj.content || []);
                totalPages = pageObj.totalPages ?? 1;
                pageIdx += 1;
            } while (pageIdx < totalPages);

            if (!allRows.length) {
                setSnack({ open: true, msg: "No hay datos que exportar", sev: "info" });
                return;
            }

            const enrichedRows = allRows.map(enrichHistoricoRow);

            const filteredRows = (histFechaEstadoDesde || histFechaEstadoHasta)
                ? enrichedRows.filter((r) =>
                    r._esEstado &&
                    isDateInRangeInclusive(
                        r._fechaEstadoIso,
                        histFechaEstadoDesde,
                        histFechaEstadoHasta
                    )
                )
                : enrichedRows;

            if (!filteredRows.length) {
                setSnack({ open: true, msg: "No hay datos que exportar con esos filtros", sev: "info" });
                return;
            }

            const rowsForXlsx = filteredRows.map((h) => {
                const colegiadoCodigo = h?.colegiado?.codigo ?? h?.colegiadoCodigo ?? "";
                const colegiadoNombre =
                    (h?.colegiado?.nombreCompleto ??
                        [h?.colegiado?.nombre, h?.colegiado?.apellido_1, h?.colegiado?.apellido_2].filter(Boolean).join(" ")
                    ) || h?.colegiadoNombre || "";

                const comentarioAdmin = h?._comentarioAdmin ?? h?.comentarioAdmin ?? h?.comentario_admin ?? "";
                const parsed = h?._parsedEstado ?? parseCambioEstado(comentarioAdmin);
                const esEstado = h?._esEstado ?? !!parsed;
                const esEstadoNuevo = h?._esEstadoNuevo ?? (esEstado && isEstadoNuevoFromParsed(parsed));

                const tipoExport = esEstado
                    ? (esEstadoNuevo ? "Estado NUEVO" : "ESTADO")
                    : (h?.tipoCambio || "Cambio");

                const transicionExport = esEstado
                    ? (esEstadoNuevo
                        ? "NUEVO"
                        : (parsed?.from || parsed?.to ? `${parsed?.from || "—"} -> ${parsed?.to || "—"}` : "—"))
                    : "—";

                const numCambios =
                    Array.isArray(h?.cambios) ? h.cambios.length
                        : h?.cambiosJson ? Object.keys(h.cambiosJson || {}).length
                            : h?.resumenCambios ? h.resumenCambios.length
                                : "";

                return {
                    ID: h?.id ?? "",
                    Fecha: formatDDMMYYYY(h?.fecha || h?.creadoEl),
                    "Código colegiado": colegiadoCodigo,
                    "Nombre colegiado": colegiadoNombre,
                    Tipo: tipoExport,
                    "Fecha Estado": esEstado ? (h?._fechaEstadoTexto || "—") : "—",
                    "Transición": transicionExport,
                    "Comentario admin": comentarioAdmin || "",
                };
            });

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.json_to_sheet(rowsForXlsx);
            XLSX.utils.book_append_sheet(wb, ws, "Histórico");

            const colWidths = Object.keys(rowsForXlsx[0]).map((key) => ({
                wch: Math.max(
                    key.length,
                    ...rowsForXlsx.map((row) => (row[key] ? row[key].toString().length : 0))
                ) + 2,
            }));
            ws["!cols"] = colWidths;

            const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            saveAs(
                new Blob([wbout], { type: "application/octet-stream" }),
                `historico_cambios_${new Date().toISOString().slice(0, 10)}.xlsx`
            );

            setSnack({ open: true, msg: "Exportación completada", sev: "success" });
        } catch (e) {
            console.error(e);
            setSnack({ open: true, msg: "Error al exportar", sev: "error" });
        }
    };

    /* -------- Exportar ESTADÍSTICAS de cambios de ESTADO (PDF) -------- */
    const exportEstadisticasEstadoPDF = async () => {
        try {
            // 1) Recolectar registros bajo filtros actuales (forzando tipo=ESTADO)
            const endpoint = `${API}/api/historico-cambios/admin/todos`;
            const pageSize = 1000;
            let pageIdx = 0, totalPages = 1;
            const baseParams = { sortBy: histSortBy, order: histSortDir, tipo: "ESTADO" };
            const codigoNum = parseInt(histCodigo, 10);
            if (!Number.isNaN(codigoNum)) baseParams.codigo = codigoNum;
            if (histDesde) baseParams.desde = histDesde;
            if (histHasta) baseParams.hasta = histHasta;
            if (histQ && histQ.trim().length >= 2) baseParams.q = histQ.trim();

            const registros = [];
            do {
                const res = await axios.get(endpoint, {
                    headers: authHeaders(),
                    params: { ...baseParams, page: pageIdx, size: pageSize },
                });
                const pageObj = normalizePage(res.data, { page: pageIdx, size: pageSize });
                registros.push(...pageObj.content);
                totalPages = pageObj.totalPages ?? 1;
                pageIdx += 1;
            } while (pageIdx < totalPages);

            if (!registros.length) {
                setSnack({ open: true, msg: "No hay datos para generar estadísticas", sev: "info" });
                return;
            }

            // 2) Contabilizar
            let totalAltasNuevas = 0;          // NUEVOS INGRESOS
            let totalAltaABaja = 0;
            let totalBajaAAlta = 0;
            let totalDefuncion = 0;
            const tiposBaja = new Map();
            const tiposAlta = new Map();

            for (const h of registros) {
                const comentario = h?.comentarioAdmin ?? h?.comentario_admin ?? "";
                const tr = parseCambioEstado(comentario);
                if (!tr && isAltaNuevaColegiacion(comentario)) {
                    totalAltasNuevas += 1;
                    continue;
                }
                if (!tr) continue;
                // Si es "Estado NUEVO" (from vacío o tipoCambio menciona nuevo) -> cuenta como NUEVOS INGRESOS
                const tipoTxt = String(h?.tipoCambio || "");
                if (isEstadoNuevoFromParsed(tr) || /nuevo/i.test(tipoTxt)) {
                    totalAltasNuevas += 1;
                    continue; // no se computa en los otros bloques
                }

                const { from, to } = tr;

                if (isDefuncion(to)) { totalDefuncion += 1; continue; }
                if (isAlta(from) && isBaja(to)) {
                    totalAltaABaja += 1;
                    tiposBaja.set(to, (tiposBaja.get(to) || 0) + 1);
                } else if (isBaja(from) && isAlta(to)) {
                    totalBajaAAlta += 1;
                    tiposAlta.set(to, (tiposAlta.get(to) || 0) + 1);
                } else if (isAlta(to)) {
                    tiposAlta.set(to, (tiposAlta.get(to) || 0) + 1);
                } else if (isBaja(to)) {
                    tiposBaja.set(to, (tiposBaja.get(to) || 0) + 1);
                }
            }

            // 3) Generar PDF (A4)
            const pdfDoc = await PDFDocument.create();
            let pdfPage = pdfDoc.addPage([595.28, 841.89]); // A4
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
            const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

            const drawText = (text, x, y, size = 11, f = font) =>
                pdfPage.drawText(sanitizePdfText(text), { x, y, size, font: f, color: rgb(0, 0, 0) });

            const newPage = () => { pdfPage = pdfDoc.addPage([595.28, 841.89]); return 800; };

            let y = 800;

            // Encabezado principal
            drawText("COLEGIO DE ENFERMERÍA DE VALLADOLID", 120, y, 18, bold);
            y -= 30;

            // Título del informe
            drawText("Estadísticas de cambios de ESTADO", 40, y, 16, bold);
            y -= 18;

            // Fecha de generación
            drawText(`Generado: ${new Date().toLocaleString("es-ES")}`, 40, y);
            y -= 14;

            // Filtros aplicados
            const filtros = [
                histDesde ? `Desde: ${histDesde}` : null,
                histHasta ? `Hasta: ${histHasta}` : null,
                histCodigo ? `Código: ${histCodigo}` : null,
                histQ ? `Búsqueda: ${histQ}` : null,
            ].filter(Boolean).join("  |  ");
            if (filtros) { drawText(`Filtros: ${filtros}`, 40, y); y -= 16; }

            y -= 6;
            drawText("Totales:", 40, y, 12, bold); y -= 16;
            drawText(`NUEVOS INGRESOS: ${totalAltasNuevas}`, 60, y); y -= 14;
            drawText(`ALTA -> BAJA: ${totalAltaABaja}`, 60, y); y -= 14;
            drawText(`BAJA -> ALTA: ${totalBajaAAlta}`, 60, y); y -= 14;
            drawText(`DEFUNCIÓN (cualquier -> Defunción): ${totalDefuncion}`, 60, y); y -= 20;

            // Desglose BAJA
            drawText("Tipos de BAJA (destino):", 40, y, 12, bold); y -= 16;
            if (tiposBaja.size === 0) { drawText("—", 60, y); y -= 14; }
            else {
                const entries = Array.from(tiposBaja.entries()).sort((a, b) => b[1] - a[1]);
                for (const [tipo, count] of entries) {
                    drawText(`${tipo}: ${count}`, 60, y); y -= 14;
                    if (y < 60) y = newPage();
                }
            }
            y -= 10;

            // Desglose ALTA
            drawText("Tipos de ALTA (destino):", 40, y, 12, bold); y -= 16;
            if (tiposAlta.size === 0) { drawText("—", 60, y); y -= 14; }
            else {
                const entries = Array.from(tiposAlta.entries()).sort((a, b) => b[1] - a[1]);
                for (const [tipo, count] of entries) {
                    drawText(`${tipo}: ${count}`, 60, y); y -= 14;
                    if (y < 60) y = newPage();
                }
            }

            const pdfBytes = await pdfDoc.save();
            saveAs(new Blob([pdfBytes], { type: "application/pdf" }),
                `estadisticas_cambios_estado_${new Date().toISOString().slice(0, 10)}.pdf`);
            setSnack({ open: true, msg: "PDF generado", sev: "success" });
        } catch (e) {
            console.error(e);
            setSnack({ open: true, msg: "Error generando PDF", sev: "error" });
        }
    };


    /* -------------------- Effects -------------------- */
    useEffect(() => { if (tab === 0) fetchPendientes(); }, [tab, pendPage, pendRows, pendSortBy, pendSortDir]); // eslint-disable-line
    useEffect(() => { if (tab === 0) { setPendPage(0); fetchPendientes(); } }, [fColeg, fEstado, fDesde, fHasta]); // eslint-disable-line
    useEffect(() => {
        if (tab === 1) fetchHistorico();
    }, [
        tab, page, rows, histSortBy, histSortDir,
        histCodigo, histTipo, histDesde, histHasta, histQ,
        histFechaEstadoDesde, histFechaEstadoHasta
    ]); // eslint-disable-line

    const toggleSelect = (id) => {
        setSelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const aprobarIds = async (ids) => {
        if (!ids.length) return;

        try {
            const solicitudes = (pendData?.content || [])
                .filter(row => ids.includes(row.id))
                .map(row => {
                    const campo = (row?.campo || "").toLowerCase();
                    const valorNuevo = row?.valorNuevo ?? row?.valor_nuevo ?? row?.valor ?? "";

                    let bic = null;

                    if (campo === "iban" && valorNuevo) {
                        const iban = String(valorNuevo)
                            .replace(/\s+/g, "")
                            .toUpperCase();

                        const codigoBanco = iban.substring(4, 8);
                        bic = obtenerBICdesdeBanco(codigoBanco);
                    }

                    return {
                        id: row.id,
                        bic
                    };
                });

            await axios.put(
                `${API}/api/cambios/aprobar`,
                solicitudes,
                { headers: authHeaders() }
            );

            setSnack({
                open: true,
                msg: `Aprobadas ${ids.length} solicitudes`,
                sev: "success"
            });

            fetchPendientes();
            if (tab === 1) fetchHistorico();

        } catch {
            setSnack({
                open: true,
                msg: "Error al aprobar",
                sev: "error"
            });
        }
    };

    const rechazarIds = async (ids, comentario) => {
        if (!ids.length) return;
        try {
            await axios.put(`${API}/api/cambios/rechazar`, { ids, comentario }, { headers: authHeaders() });
            setSnack({ open: true, msg: `Rechazadas ${ids.length} solicitudes`, sev: "success" });
            fetchPendientes();
        } catch {
            setSnack({ open: true, msg: "Error al rechazar", sev: "error" });
        }
    };

    const pendCount = safeCount(pendData);
    const histCount = safeCount(hist);

    return (
        <Paper sx={{ p: 0, overflow: "hidden" }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Pendientes" />
                <Tab icon={<HistoryIcon />} iconPosition="start" label="Histórico" />
                <Tab icon={<HistoryIcon />} iconPosition="start" label="Antiguo Histórico" />
            </Tabs>

            {/* ----- PENDIENTES ----- */}
            {tab === 0 && (
                <>
                    <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>Solicitudes pendientes</Typography>

                        <TextField size="small" label="Código colegiado (filtro 'codigo')" value={fColeg} onChange={(e) => setFColeg(e.target.value)} sx={{ width: 160 }} />
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="f-estado-label">Estado</InputLabel>
                            <Select labelId="f-estado-label" label="Estado" value={fEstado} onChange={(e) => setFEstado(e.target.value)}>
                                <MenuItem value="">(todos)</MenuItem>
                                <MenuItem value="PENDIENTE">PENDIENTE</MenuItem>
                                <MenuItem value="ADMIN">ADMIN</MenuItem>
                                <MenuItem value="APROBADO">APROBADO</MenuItem>
                                <MenuItem value="RECHAZADO">RECHAZADO</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={fDesde} onChange={(e) => setFDesde(e.target.value)} sx={{ width: 160 }} />
                        <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={fHasta} onChange={(e) => setFHasta(e.target.value)} sx={{ width: 160 }} />

                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="sort-campo-label">Ordenar por</InputLabel>
                            <Select labelId="sort-campo-label" label="Ordenar por" value={pendSortBy} onChange={(e) => { setPendSortBy(e.target.value); setPendPage(0); }} startAdornment={<SortIcon sx={{ mr: 1 }} />}>
                                <MenuItem value="id">ID</MenuItem>
                                <MenuItem value="campo">Campo</MenuItem>
                                <MenuItem value="fechaSolicitud">Fecha (solicitud)</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel id="sort-dir-label">Dirección</InputLabel>
                            <Select labelId="sort-dir-label" label="Dirección" value={pendSortDir} onChange={(e) => { setPendSortDir(e.target.value); setPendPage(0); }}>
                                <MenuItem value="DESC">Desc</MenuItem>
                                <MenuItem value="ASC">Asc</MenuItem>
                            </Select>
                        </FormControl>

                        <Tooltip title="Refrescar"><IconButton onClick={fetchPendientes}><RefreshIcon /></IconButton></Tooltip>
                        <Button variant="outlined" startIcon={<HistoryIcon />} onClick={exportPendientesToExcel}>Exportar Excel</Button>
                        <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} disabled={!selIds.length} onClick={() => aprobarIds(selIds)}>Aprobar seleccionados</Button>
                        <Button variant="outlined" color="error" startIcon={<CancelIcon />} disabled={!selIds.length} onClick={() => { setRejectTargetIds(selIds); setRechazoOpen(true); }}>Rechazar seleccionados</Button>
                    </Toolbar>

                    <TableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selIds.length > 0 && selIds.length < (pendData?.content?.length || 0)}
                                            checked={(pendData?.content?.length || 0) > 0 && selIds.length === (pendData?.content?.length || 0)}
                                            onChange={(e) => setSelIds(e.target.checked ? (pendData?.content || []).map((x) => x.id) : [])}
                                        />
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Colegiado</TableCell>
                                    <TableCell>Campo / Fecha</TableCell>
                                    <TableCell>Valores</TableCell>
                                    <TableCell align="right">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(pendData?.content || []).map((row) => (
                                    <FilaPendiente
                                        key={row.id}
                                        row={{
                                            ...row,
                                            onApprove: () => aprobarIds([row.id]),
                                            onReject: () => { setRejectTargetIds([row.id]); setRechazoOpen(true); },
                                            onPreview: (which) => openPreview(row, which),
                                        }}
                                        selected={selIds.includes(row.id)}
                                        toggle={id => {
                                            setSelIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
                                        }}
                                    />
                                ))}
                                {!((pendData?.content || []).length) && !pendLoading && (
                                    <TableRow><TableCell colSpan={6} align="center"><Typography color="text.secondary">No hay solicitudes.</Typography></TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={pendCount}
                        page={pendPage}
                        onPageChange={(_, p) => { setPendPage(p); setSelIds([]); }}
                        rowsPerPage={pendRows}
                        onRowsPerPageChange={(e) => { setPendRows(parseInt(e.target.value, 10)); setPendPage(0); setSelIds([]); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Filas por página"
                    />
                </>
            )}

            {/* ----- HISTÓRICO ----- */}
            {tab === 1 && (
                <>
                    <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>Histórico de cambios</Typography>

                        <TextField
                            size="small"
                            label="Código colegiado"
                            value={histCodigo}
                            onChange={(e) => { setHistCodigo(e.target.value); setPage(0); }}
                            sx={{ width: 160 }}
                        />

                        {/* Búsqueda libre */}
                        <TextField
                            size="small"
                            label="Buscar"
                            placeholder="Nombre, NIF, email, comentario…"
                            value={histQ}
                            onChange={(e) => { setHistQ(e.target.value); setPage(0); }}
                            sx={{ width: 260 }}
                        />

                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="hist-tipo-label">Tipo</InputLabel>
                            <Select
                                labelId="hist-tipo-label"
                                label="Tipo"
                                value={histTipo}
                                onChange={(e) => { setHistTipo(e.target.value); setPage(0); }}
                            >
                                <MenuItem value="">(todos)</MenuItem>
                                <MenuItem value="ESTADO">ESTADO</MenuItem>
                                <MenuItem value="CAMBIO">Cambio</MenuItem>
                            </Select>
                        </FormControl>

                        <TextField
                            size="small"
                            type="date"
                            label="Desde"
                            InputLabelProps={{ shrink: true }}
                            value={histDesde}
                            onChange={(e) => { setHistDesde(e.target.value); setPage(0); }}
                            sx={{ width: 160 }}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="Hasta"
                            InputLabelProps={{ shrink: true }}
                            value={histHasta}
                            onChange={(e) => { setHistHasta(e.target.value); setPage(0); }}
                            sx={{ width: 160 }}
                        />
                        <TextField
                            size="small"
                            type="date"
                            label="Fecha estado desde"
                            InputLabelProps={{ shrink: true }}
                            value={histFechaEstadoDesde}
                            onChange={(e) => { setHistFechaEstadoDesde(e.target.value); setPage(0); }}
                            sx={{ width: 180 }}
                        />

                        <TextField
                            size="small"
                            type="date"
                            label="Fecha estado hasta"
                            InputLabelProps={{ shrink: true }}
                            value={histFechaEstadoHasta}
                            onChange={(e) => { setHistFechaEstadoHasta(e.target.value); setPage(0); }}
                            sx={{ width: 180 }}
                        />

                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel id="hist-sortby-label">Ordenar por</InputLabel>
                            <Select
                                labelId="hist-sortby-label" label="Ordenar por"
                                value={histSortBy}
                                onChange={(e) => { setHistSortBy(e.target.value); setPage(0); }}
                            >
                                <MenuItem value="fecha">Fecha</MenuItem>
                                <MenuItem value="id">ID</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel id="hist-sortdir-label">Dirección</InputLabel>
                            <Select
                                labelId="hist-sortdir-label" label="Dirección"
                                value={histSortDir}
                                onChange={(e) => { setHistSortDir(e.target.value); setPage(0); }}
                            >
                                <MenuItem value="desc">Desc</MenuItem>
                                <MenuItem value="asc">Asc</MenuItem>
                            </Select>
                        </FormControl>

                        {/* EXPORTS */}
                        <Button variant="outlined" startIcon={<HistoryIcon />} onClick={exportHistoricoToExcel}>
                            Exportar Excel
                        </Button>

                        {histTipo === "ESTADO" && (
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<PictureAsPdfIcon />}
                                onClick={exportEstadisticasEstadoPDF}
                            >
                                Exportar estadísticas (PDF)
                            </Button>
                        )}

                        <Button
                            variant="text"
                            startIcon={<FilterAltOffIcon />}
                            onClick={() => {
                                setHistCodigo("");
                                setHistQ("");
                                setHistTipo("");
                                setHistDesde("");
                                setHistHasta("");
                                setHistSortBy("fecha");
                                setHistSortDir("desc");
                                setRows(25);
                                setPage(0);
                                setHistFechaEstadoDesde("");
                                setHistFechaEstadoHasta("");
                            }}
                        >
                            Limpiar filtros
                        </Button>

                        <Tooltip title="Refrescar"><IconButton onClick={fetchHistorico}><RefreshIcon /></IconButton></Tooltip>
                    </Toolbar>

                    <TableContainer>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Fecha</TableCell>
                                    <TableCell>Colegiado</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Fecha Estado</TableCell>
                                    <TableCell>Transición</TableCell>
                                    <TableCell>Comentario admin</TableCell>
                                    <TableCell>Resumen</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {hist?.content?.map((h) => {
                                    const colegiadoCodigo = h?.colegiado?.codigo ?? h?.colegiadoCodigo ?? "-";
                                    const colegiadoNombre =
                                        (h?.colegiado?.nombreCompleto ??
                                            [h?.colegiado?.nombre, h?.colegiado?.apellido_1, h?.colegiado?.apellido_2].filter(Boolean).join(" ")
                                        ) || h?.colegiadoNombre || "-";
                                    const numCambios =
                                        Array.isArray(h?.cambios) ? h.cambios.length
                                            : h?.cambiosJson ? Object.keys(h.cambiosJson || {}).length
                                                : h?.resumenCambios ? h.resumenCambios.length : undefined;

                                    const comentarioAdmin = h?._comentarioAdmin ?? h?.comentarioAdmin ?? h?.comentario_admin ?? "";
                                    const parsed = h?._parsedEstado ?? parseCambioEstado(comentarioAdmin);
                                    const esEstado = h?._esEstado ?? !!parsed;
                                    const esEstadoNuevo = h?._esEstadoNuevo ?? (esEstado && isEstadoNuevoFromParsed(parsed));
                                    const fechaEstadoTexto = h?._fechaEstadoTexto ?? "—";

                                    const transicionLabel = esEstado
                                        ? (esEstadoNuevo ? "NUEVO" : (parsed ? `${parsed.from} -> ${parsed.to}` : "—"))
                                        : "—";

                                    const tipoExport = esEstado ? (esEstadoNuevo ? "Estado NUEVO" : "ESTADO")
                                        : (h?.tipoCambio || "Cambio");
                                    const transicionExport = esEstado ? (esEstadoNuevo ? "NUEVO" : `${parsed.from} -> ${parsed.to}`) : "";

                                    return (
                                        <TableRow key={h.id} hover>
                                            <TableCell width={50}>
                                                <Tooltip title="Eliminar registro">
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        onClick={() => eliminarHistoricoPorSql(h.id)}
                                                    >
                                                        <DeleteForeverIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>

                                            <TableCell>{h.id}</TableCell>
                                            <TableCell>{formatDDMMYYYY(h?.fecha || h?.creadoEl)}</TableCell>
                                            <TableCell>
                                                <Stack spacing={0.3}>
                                                    <Typography fontWeight={600}>{colegiadoNombre}</Typography>
                                                    <Typography variant="caption" color="text.secondary">Código: {colegiadoCodigo}</Typography>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    {esEstado ? (
                                                        <Chip
                                                            size="small"
                                                            label={esEstadoNuevo ? "Estado NUEVO" : "ESTADO"}
                                                            sx={{
                                                                bgcolor: esEstadoNuevo ? 'success.dark' : 'warning.dark',
                                                                color: 'common.white',
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip size="small" label={h?.tipoCambio || "Cambio"} />
                                                    )}
                                                </Stack>
                                            </TableCell>

                                            <TableCell>
                                                {esEstado ? (
                                                    <Chip
                                                        size="small"
                                                        color="info"
                                                        variant="outlined"
                                                        label={fechaEstadoTexto}
                                                        onClick={() => {
                                                            const nueva = window.prompt(
                                                                "Nueva fecha (DD-MM-YYYY)",
                                                                (h?._fechaEstadoIso || "").split("-").reverse().join("-")
                                                            );

                                                            if (!nueva) return;

                                                            const iso = dmyToIso(nueva);

                                                            if (!iso) {
                                                                setSnack({
                                                                    open: true,
                                                                    msg: "Formato incorrecto. Usa DD-MM-YYYY",
                                                                    sev: "error"
                                                                });
                                                                return;
                                                            }

                                                            editarFechaHistoricoPorSql(h, iso);
                                                        }}
                                                        sx={{ cursor: "pointer" }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                                )}
                                            </TableCell>

                                            <TableCell sx={{ maxWidth: 280 }}>
                                                {esEstado ? (
                                                    <Chip
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        label={transicionLabel}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">—</Typography>
                                                )}
                                            </TableCell>

                                            <TableCell sx={{ maxWidth: 360 }}>
                                                <Typography variant="body2" noWrap title={comentarioAdmin || ""}>
                                                    {esEstado && !esEstadoNuevo && parsed
                                                        ? `ESTADO: de "${parsed.from}" a "${parsed.to}" — ${comentarioAdmin || "—"}`
                                                        : (comentarioAdmin || "—")}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {numCambios != null
                                                    ? <Chip size="small" variant="outlined" label={`${numCambios} campo(s)`} />
                                                    : <Typography variant="body2" color="text.secondary">—</Typography>}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                                {!hist?.content?.length && !histLoading && (
                                    <TableRow><TableCell colSpan={9} align="center"><Typography color="text.secondary">Sin registros.</Typography></TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={histCount}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rows}
                        onRowsPerPageChange={(e) => { setRows(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="Filas por página"
                    />

                </>
            )}

            {/* ----- ANTIGUO HISTÓRICO ----- */}
            {tab === 2 && (
                <AntiguoHistoricoAdmin />
            )}

            {/* Diálogo rechazo */}
            <RechazoDialog
                open={rechazoOpen}
                onClose={() => setRechazoOpen(false)}
                onConfirm={(comentario) => {
                    const ids = rejectTargetIds;
                    setRechazoOpen(false);
                    setRejectTargetIds([]);
                    rechazarIds(ids, comentario);
                }}
            />

            {/* Diálogo PREVIEW FOTO */}
            <Dialog open={previewOpen} onClose={closePreview} maxWidth="sm" fullWidth>
                <DialogTitle>{previewTitle || "Previsualización"}</DialogTitle>
                <DialogContent dividers sx={{ display: "flex", justifyContent: "center", minHeight: 200 }}>
                    {previewLoading ? <CircularProgress /> :
                        previewUrl ? <img src={previewUrl} alt="Previsualización" style={{ maxWidth: "100%", height: "auto", borderRadius: 8 }} /> :
                            <Typography color="text.secondary">Sin imagen</Typography>}
                </DialogContent>
                <DialogActions>
                    {previewUrl && (
                        <Button component={Link} href={previewUrl} target="_blank" rel="noopener" variant="text">
                            Abrir en nueva pestaña
                        </Button>
                    )}
                    <Button onClick={closePreview}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* Snack */}
            <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
                <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.sev} variant="filled" sx={{ width: "100%" }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Paper>
    );
}
